import * as React from 'react';
import { useForm } from '../../../hooks/useForm';
import { Checkbox, Input } from '../../common/Form';
import { useApiRequest } from '../../../hooks/useApiRequest';
import { api, DOMAIN } from '../../../lib/api';
import { noop } from '../../../utils';
import { FComponent, RecursiveToCamel } from '../../../types/common';
import { CaptchaToken } from '../../unauthenticated/SliderCaptcha';
import { AuthState } from './FrictionlessPacket';
import { ErrorMessage } from '../../common/Form/ErrorMessage';
import { isEmail, isValueInSet } from '../../../utils/validators';
import { Validators, Schema } from 'tiny-validation';
import { ApiErrors, isApiError } from '../../../lib/api/types';
import { ChangeNoticeState, LATEST_CHANGE } from '../../../hooks/useChangeNotice';
import { store } from '../../../lib/store';
import { localStorageKeys } from '@hooks/useLocalStorageState';

type RegisterRequest = RecursiveToCamel<
  {
    seller: {
      email: string;
      tos_accepted: boolean;
      referral_code: string;
      external_id?: string;
    };
    subject: string;
  } & CaptchaToken
>;

type LoginRequest = RecursiveToCamel<
  {
    seller: {
      email: string;
      password: string;
      referral_code: string;
      external_id?: string;
    };
    subject: string;
  } & CaptchaToken
>;

type AuthResponse = {
  token: string;
  pubkey: string;
};

type EmailAvailabilityRequest = {
  email: string;
} & CaptchaToken;

type EmailAvailabilityResponse = {
  message: string;
};

type AuthenticationFormData = {
  email: string;
  password?: string;
  tosAccepted?: string;
};

export const useFrictionlessAuth = (
  userEmail: string,
  pubkey: string,
  authToken: string
) => {
  const [auth, updateAuth] = React.useReducer(
    (prev: AuthState, update: Partial<AuthState>) => ({ ...prev, ...update }),
    getInitialState(userEmail, authToken, pubkey)
  );
  const captchaTokenRef = React.useRef<CaptchaToken>(null);
  const [captchaError, setCaptchaError] = React.useState<ApiErrors>(null);

  const checkEmailReq = useApiRequest<
    EmailAvailabilityRequest,
    EmailAvailabilityResponse
  >({
    method: 'POST',
    endpoint: api.unauthenticated.checkEmailAvailability
  });
  const registerReq = useApiRequest<RegisterRequest, AuthResponse>({
    method: 'POST',
    endpoint: api.seller.signup
  });
  const loginReq = useApiRequest<LoginRequest, AuthResponse>({
    method: 'POST',
    endpoint: api.seller.login
  });
  const logoutReq = useApiRequest<Record<string, never>, void>({
    method: 'DELETE',
    endpoint: api.seller.logout
  });

  const {
    handleFieldChange: handleAuthFormFieldChange,
    isDisabled: isAuthIncomplete,
    isSubmitting: isAuthFormSubmitting,
    values: authFormValues,
    isFieldVisited: isAuthFormFieldVisited,
    errors: authFormErrors,
    reset: resetAuthForm
  } = useForm<AuthenticationFormData>({
    onSubmit: () => null,
    schema: React.useMemo(() => getAuthenticationFormSchema(auth.state), [auth.state]),
    initialValues: {
      email: userEmail || '',
      password: '',
      tosAccepted: 'false'
    }
  });

  const checkEmail = () =>
    checkEmailReq
      .request({ data: { email: authFormValues.email, ...captchaTokenRef.current } })
      .catch(error => {
        if (isEmailTakenResult(error)) {
          updateAuth({ state: 'login' });
          return { message: 'emailExists' } as EmailAvailabilityResponse;
        } else {
          throw error;
        }
      });

  const register = (packetId: string, referralCode: string, externalId: string) =>
    registerReq
      .request({
        data: {
          seller: {
            email: authFormValues.email,
            tosAccepted: authFormValues.tosAccepted === 'true',
            referralCode,
            externalId: externalId || undefined
          },
          subject: packetId,
          ...captchaTokenRef.current
        }
      })
      .then(async ({ token, pubkey }) => {
        store.setItem<ChangeNoticeState>(localStorageKeys.changeNotice, {
          currentVersion: LATEST_CHANGE.version,
          firstSeen: Date.now(),
          dismissed: true
        });
        updateAuth({
          state: 'authenticated',
          email: authFormValues.email,
          token,
          pubkey
        });
        return { token, pubkey };
      });

  const login = (packetId: string, referralCode: string, externalId: string) =>
    loginReq
      .request({
        data: {
          seller: {
            email: authFormValues.email,
            password: authFormValues.password,
            referralCode,
            externalId: externalId || undefined
          },
          subject: packetId,
          ...captchaTokenRef.current
        }
      })
      .then(({ token, pubkey }) => {
        updateAuth({
          state: 'authenticated',
          email: authFormValues.email,
          token,
          pubkey
        });
        return { token, pubkey };
      });

  const resetErrorState = () => {
    checkEmailReq.setError(null);
    registerReq.setError(null);
    loginReq.setError(null);
    logoutReq.setError(null);
    setCaptchaError(null);
  };

  const resetAuth = () => {
    resetAuthForm({ email: '', password: '', tosAccepted: 'false' });
    resetErrorState();
    updateAuth({ state: 'unauthenticated', email: '', token: '', pubkey: '' });
  };

  const handleResetAuthForm = () => {
    if (auth.state === 'signedIn' || auth.state === 'authenticated') {
      logoutReq
        .request()
        .then(() => resetAuth())
        .catch(noop);
    } else {
      resetAuth();
    }
  };

  const apiError = registerReq.error || loginReq.error || logoutReq.error || captchaError;

  const showApiError =
    registerReq.isError || loginReq.isError || logoutReq.isError || !!captchaError;

  return {
    authFormProps: {
      auth,
      updateAuth,
      handleAuthFormFieldChange,
      isAuthIncomplete,
      authFormValues,
      authFormErrors,
      isAuthFormFieldVisited,
      resetAuthForm,
      isAuthFormSubmitting,
      handleResetAuthForm,
      checkEmailError: checkEmailReq.error,
      isCheckEmailError: checkEmailReq.isError,
      apiError,
      showApiError
    },
    checkEmail,
    login,
    register,
    captchaTokenRef,
    captchaError,
    setCaptchaError,
    resetErrorState
  };
};

type FrictionlessAuthProps = ReturnType<typeof useFrictionlessAuth>;

const WrappedFrictionlessAuth: FComponent<
  FrictionlessAuthProps['authFormProps'] & {
    isExchange: boolean;
    authCopy?: Record<AuthState['state'], string>;
  }
> = (
  {
    auth,
    authFormValues,
    handleAuthFormFieldChange,
    handleResetAuthForm,
    isAuthFormFieldVisited,
    authFormErrors,
    checkEmailError,
    isCheckEmailError,
    isExchange,
    authCopy = {}
  },
  ref: React.MutableRefObject<HTMLInputElement>
) => {
  return (
    <div>
      <div className="auth-section">
        {isExchange ? null : (
          <div className="auth-legend">
            <div className="title">{authStrings[auth.state].title}</div>
            <div className="subtitle">
              {authCopy[auth.state] || authStrings[auth.state].subtitle}
            </div>
          </div>
        )}
        <div className="auth-email-input">
          <Input
            type="email"
            name="email"
            label="Email"
            onChange={handleAuthFormFieldChange}
            value={authFormValues['email']}
            placeholder="peter@bugle.net"
            disabled={auth.state !== 'unauthenticated'}
            visited={isAuthFormFieldVisited('email')}
            autoCapitalize={false}
            errors={authFormErrors['email']}
          />
          {['signedIn', 'authenticated'].includes(auth.state) && !isExchange ? (
            <button type="button" className="change-button" onClick={handleResetAuthForm}>
              Change
            </button>
          ) : null}
        </div>
        {auth.state === 'login' ? (
          <Input
            ref={ref}
            type="password"
            label="Password"
            name="password"
            onChange={handleAuthFormFieldChange}
            value={authFormValues['password']}
            visited={isAuthFormFieldVisited('password')}
            errors={authFormErrors['password']}
            peekPassword
          />
        ) : null}
        {auth.state === 'unauthenticated' ? (
          <Checkbox
            name="tosAccepted"
            label={
              <div className="tos-label">
                I accept TARTLE&apos;s{' '}
                <a target="_blank" rel="noreferrer" href={`${DOMAIN}/terms_of_service`}>
                  terms of service
                </a>{' '}
                and{' '}
                <a target="_blank" rel="noreferrer" href={`${DOMAIN}/privacy_policy`}>
                  privacy policy
                </a>
              </div>
            }
            checked={authFormValues['tosAccepted'] === 'true'}
            onChange={handleAuthFormFieldChange}
            visited={isAuthFormFieldVisited('tosAccepted')}
            errors={authFormErrors['tosAccepted']}
          />
        ) : null}
      </div>
      <ErrorMessage
        errors={checkEmailError}
        isShowing={isCheckEmailError}
        keysToFilter={['email']}
      />
    </div>
  );
};

export const FrictionlessAuth = React.forwardRef(WrappedFrictionlessAuth);

const getAuthenticationFormSchema = (authState: AuthState['state']) => {
  const { isPresent } = Validators;

  const schema: Schema = {
    email: [isPresent('Enter your email'), isEmail()]
  };

  if (authState === 'login') {
    schema['password'] = [isPresent('Enter your password')];
  }
  if (authState === 'unauthenticated') {
    schema['tosAccepted'] = [
      isValueInSet(['true'], 'You must agree to the Terms of Service to continue')
    ];
  }
  return schema;
};

// returns true if the email is verified as taken based on an api result
const isEmailTakenResult = (error: ApiErrors) => {
  if (isApiError(error) && Array.isArray(error)) return false;
  return error?.['email']?.[0] === 'already taken';
};

const authStrings = {
  login: {
    title: 'Welcome back',
    subtitle:
      'Looks like you already have a TARTLE account, please enter your password below to continue'
  },
  unauthenticated: {
    title: '',
    subtitle: 'Securely save your data to get paid on TARTLE'
  },
  authenticated: {
    title: '',
    subtitle: 'Your data will be saved to your TARTLE account'
  },
  signedIn: {
    title: '',
    subtitle: "You're already signed into TARTLE"
  }
};

const getInitialState = (userEmail: string, token = '', pubkey: string): AuthState => {
  let state: AuthState['state'] = 'unauthenticated';
  if (token) {
    state = 'authenticated';
  } else if (userEmail) {
    state = 'signedIn';
  }
  return {
    state,
    email: userEmail,
    token,
    pubkey
  };
};
